<template>
  <v-form v-model="valid" ref="DialogForm" lazy-validation>
    <slotted-dialog :title="'Send email'" :externaltrigger="dialogtrigger" :external-close-trigger="closetrigger">
      <template v-slot:content>
        <v-container :key="dialogtrigger" v-if="editObject">
          <v-row dense>
            <v-col cols="12" class="mt-2">
              <v-combobox v-model="editObject.To" :items="toItems" :search-input.sync="toQuery"
                :label="$t('servicesystem.common_objects.commonterms.to')" :rules="[requiredRule, emailRule]" persistent-placeholder
                :item-text="'text'" :item-value="'value'" hide-details="auto" :return-object="false" clearable hide-selected hide-no-data dense
                :loading="toLoading" @change="onToChanged"
                :error="toErrorMessages.length > 0" :error-messages="toErrorMessages">
              </v-combobox>
            </v-col>

            <v-col cols="12">
              <QTextField v-model="editObject.Subject" :label="$t('servicesystem.common_objects.email_settings.header')"
                :rules="[requiredRule]" externaledit>
              </QTextField>
            </v-col>

            <v-col cols="12" class="my-2 d-flex">
              <v-icon size="20" class="mr-1">mdi-paperclip</v-icon>
              <v-chip small color="primary">{{ editObject.Attachments.length }} Attachments</v-chip>
            </v-col>

            <v-col cols="12">
              <v-textarea v-model="editObject.Body" :label="$t('servicesystem.common_objects.email_settings.body')"
                :rules="[requiredRule]" outlined rows="6" auto-grow />

            </v-col>

          </v-row>
        </v-container>
      </template>

      <template v-slot:buttons>
        <v-btn @click="closetrigger++" color="primary" outlined
          v-text="$t('servicesystem.common_objects.commonterms.cancel')">
        </v-btn>
        <v-btn :loading="loading" :disabled="!valid" :color="valid ? 'quaternary' : 'disabled'" :dark="!!valid"
          @click="valid && !loading ? save() : null">
          {{ $t('servicesystem.common_objects.commonterms.send') }}
        </v-btn>
      </template>
    </slotted-dialog>
  </v-form>
</template>

<script>
import clientAPI from "@/api/ClientAPIMixin.js";
import QInputRuleMixin from "@/helpers/Mixins/QInputRuleMixin.js";
import SlottedDialog from "@/views/layouts/SlottedDialog.vue";

export default {
  name: "SendEmailDialog",
  props: {
    externaltrigger: Number,
    emailDto: Object,
    saveFunction: { type: Function, required: true },
  },
  components: { SlottedDialog },
  mixins: [clientAPI, QInputRuleMixin],

  data() {
    return {
      dialogtrigger: 0,
      closetrigger: 0,
      editObject: null,
      valid: false,
      loading: false,
      toQuery: "",
      toItems: [],
      toLoading: false,
      toAllContactsCache: [],
      toAbortCtrl: null,
      toErrorMessages: [],
    };
  },

  methods: {
    parse() {
      const src = this.emailDto || {};
      this.editObject = {
        CustomerId: src.CustomerId,
        To: src.To || "",
        CC: src.CC || "",
        Subject: src.Subject || "",
        Body: src.Body || "",
        // Attachments are read-only here; pass through as provided
        Attachments: Array.isArray(src.Attachments) ? src.Attachments : [],
      };
      // seed suggestions
      this.toItems = [];
      this.toQuery = this.editObject.To || "";
      this.prefetchSuggestions();
      this.validateToLive();
    },

    async save() {
      // Ensure form validation state is fresh
      const ok = await this.$refs.DialogForm.validate();
      if (!ok) return;

      this.loading = true;
      const dto = { ...this.editObject }; // keep shape unchanged
      // normalize To in case combobox passed an object
      if (dto && typeof dto.To === 'object' && dto.To) {
        dto.To = dto.To.value || dto.To.text || '';
      }
      await this.saveFunction(dto)
        .then(() => { this.closetrigger++; })
        .finally(() => { this.loading = false; });
    },

    async prefetchSuggestions() {
      const customerId = this.emailDto?.CustomerId;
      if (customerId) {
        try {
          this.toLoading = true;
          const contacts = await this.getCustomerContactsByCustomerId(customerId);
          this.toAllContactsCache = Array.isArray(contacts) ? contacts : [];
          this.buildItemsFromContacts(this.filteredContacts(this.toQuery, this.toAllContactsCache));
        } finally {
          this.toLoading = false;
        }
      } else {
        this.toAllContactsCache = [];
        this.toItems = [];
      }
    },

    filteredContacts(query, contacts) {
      const q = (query || "").toLowerCase();
      if (!q) return (contacts || []).slice(0, 15);
      return (contacts || []).filter(c => {
        const meta = `${c?.Id || ""} ${c?.Name || ""} ${c?.DisplayName || ""} ${c?.Title || ""} ${c?.Mobile || ""} ${c?.Email || ""}`.toLowerCase();
        return meta.includes(q);
      }).slice(0, 15);
    },

    buildItemsFromContacts(contacts) {
      const seen = new Set();
      const items = [];
      (contacts || []).forEach(c => {
        const email = (c?.Email || "").trim();
        if (!email || seen.has(email)) return;
        seen.add(email);
        const name = c?.DisplayName || c?.Name || "";
        items.push({ text: name ? `${name} <${email}>` : email, value: email });
      });
      this.toItems = items;
    },

    onToChanged(val) {
      // Coerce model to string if the component gives an object
      if (val && typeof val === 'object') {
        const str = val.value || val.text || '';
        this.editObject.To = str;
      }
      this.validateToLive();
    },

    validateToLive() {
      clearTimeout(this._toValidateTimer);
      this._toValidateTimer = setTimeout(() => {
        const candidate = (this.toQuery !== undefined && this.toQuery !== null) ? String(this.toQuery) : String(this.editObject?.To || '');
        const trimmed = candidate.trim();
        const next = [];
        // Use the same rule functions used by the component's rules prop
        const req = this.requiredRule(trimmed);
        if (req !== true) {
          next.push(req);
        } else {
          const em = this.emailRule(trimmed);
          if (em !== true) {
            next.push(em);
          }
        }
        this.toErrorMessages = next;
      }, 250);
    },
  },

  watch: {
    externaltrigger() {
      this.parse();
      this.dialogtrigger++;
    },
    toQuery: {
      handler: function (val) {
        const run = async () => {
          if (this.emailDto?.CustomerId) {
            this.buildItemsFromContacts(this.filteredContacts(val, this.toAllContactsCache));
          } else {
            // No customerId: do not search globally; keep free-typed value only
            this.toItems = [];
          }
          this.validateToLive();
        };
        clearTimeout(this._toQueryTimer);
        this._toQueryTimer = setTimeout(run, 250);
      },
      immediate: false,
    },
    'editObject.To': function () {
      this.validateToLive();
    },
  },
};
</script>